import { cloneDeep, unset } from 'lodash-es';
import type {
  ComputeProvisioningNestedProblem,
  ComputeProvisioningProblem,
  ComputeProvisioningRequiredSecretInput,
  ComputeProvisioningReviewedExternalStorage,
  ComputeProvisioningReviewedRequest
} from '@schema';
import { NETWORK_PHYSICAL_TYPES } from '@/common/constants/networks.constants';
import {
  VOLUME_DRIVER_TO_STORAGE_MAP,
  EXTERNAL_STORAGES,
  TRANSPORT_TYPE,
  VOLUME_DRIVERS
} from '@/common/constants/externalStorages.constants';
import { determineDriverInfo, resolveManagementAddress } from '@/common/store/pinia/computeExternalStorages/computeExternalStoragesMapper';
import type { ExternalStorageFormModel, PROTOCOL } from '@/common/composables/useExternalStorage';
import type {
  ComputeClusterFormModel,
  ComputeServicesModel
} from '@/common/store/pinia/computeProvisioning';
import type {
  NodeInterfaceEdits,
  NodeNetworkRolesets,
  RolesetTrafficRoles
} from '@/hci/pages/infrastructure/nodes/createComputeClusterWizard/nodeNetworkMatrix';
import { getRequestedStoragePolicies } from '@/hci/pages/infrastructure/nodes/createComputeClusterWizard/computeClusterStoragePolicyRedundancy';

export const parsePointer = (pointer: string): string[] => {
  if (!pointer || pointer === '/') {
    return [];
  }
  const raw = pointer.startsWith('/') ? pointer.slice(1) : pointer;
  return raw.split('/').map(segment => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
};

export const getByPointer = (source: any, pointer: string): any => parsePointer(pointer)
  .reduce((value, segment) => (value === undefined || value === null ? undefined : value[segment]), source);

export const setByPointer = (target: Record<string, any>, pointer: string, value: any): void => {
  const segments = parsePointer(pointer);
  if (!segments.length) {
    return;
  }
  let cursor = target;
  segments.slice(0, -1).forEach((segment, index) => {
    const nextSegment = segments[index + 1];
    if (cursor[segment] === undefined || cursor[segment] === null || typeof cursor[segment] !== 'object') {
      cursor[segment] = /^\d+$/.test(nextSegment) ? [] : {};
    }
    cursor = cursor[segment];
  });
  cursor[segments[segments.length - 1]] = value;
};

// Segments up to the first one the edited request omits, e.g. the whole subnet when IP management is disabled
const missingSubtreeSegments = (source: any, pointer: string): string[] => {
  const segments = parsePointer(pointer);
  let cursor = source;
  const depth = segments.findIndex((segment) => {
    const next = cursor?.[segment];
    if (next === undefined || next === null) {
      return true;
    }
    cursor = next;
    return false;
  });
  return segments.slice(0, depth === -1 ? segments.length : depth + 1);
};

export const applyEditablePaths = <T extends Record<string, any>>(
  baseline: T,
  edited: Record<string, any>,
  editablePaths: string[] = []
): T => {
  const result = cloneDeep(baseline);
  editablePaths.forEach((pointer) => {
    const value = getByPointer(edited, pointer);
    if (value === undefined) {
      unset(result, missingSubtreeSegments(edited, pointer));
      return;
    }
    setByPointer(result, pointer, cloneDeep(value));
  });
  return result;
};

type StepPathEntry = { prefix: string; steps: string[] };

const STEP_PATH_MAP: StepPathEntry[] = [
  { prefix: '/vm_ha_planned', steps: ['nodeSelection'] },
  { prefix: '/network', steps: ['nodeNetwork', 'configureNics'] },
  { prefix: '/storage_cluster/cluster_name', steps: ['clusterName'] },
  { prefix: '/storage_cluster/nodes', steps: ['storageConfiguration'] },
  { prefix: '/storage_cluster', steps: ['clusterName', 'storageConfiguration'] },
  { prefix: '/storage_policies', steps: ['storagePolicy'] },
  { prefix: '/external_storages', steps: ['externalStorage'] },
  { prefix: '/multipath', steps: ['externalStorage'] },
  { prefix: '/compute_cluster/external_network/subnet', steps: ['dhcpAndDns'] },
  { prefix: '/compute_cluster/external_network', steps: ['computeNetwork'] },
  { prefix: '/compute_cluster/enable_features', steps: ['addOnServices'] },
  { prefix: '/compute_cluster', steps: ['computeNetwork', 'dhcpAndDns', 'addOnServices'] }
];

const matchesPrefix = (pointer: string, prefix: string): boolean => pointer === prefix || pointer.startsWith(`${prefix}/`);

export const stepNamesForPointer = (pointer?: string | null): string[] => {
  if (!pointer) {
    return [];
  }
  const match = [...STEP_PATH_MAP]
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find(entry => matchesPrefix(pointer, entry.prefix));
  return match?.steps || [];
};

export const stepNamesForEditablePaths = (editablePaths: string[] = []): string[] => [
  ...new Set(editablePaths.flatMap(stepNamesForPointer))
];

type ExternalStorageWithSecrets = {
  client_ref: string;
  secret_params?: Record<string, unknown>;
  ssl_ca_cert_bundle?: string | null;
};

export const applyRequiredSecretInputs = <T extends { external_storages?: ExternalStorageWithSecrets[] }>(
  merged: T,
  edited: { external_storages?: ExternalStorageWithSecrets[] },
  requiredSecretInputs: ComputeProvisioningRequiredSecretInput[] = []
): T => {
  requiredSecretInputs.forEach(({ client_ref: clientRef }) => {
    const target = merged.external_storages?.find(item => item.client_ref === clientRef);
    const source = edited.external_storages?.find(item => item.client_ref === clientRef);
    if (!target || !source) {
      return;
    }
    target.secret_params = cloneDeep(source.secret_params || {});
    if (source.ssl_ca_cert_bundle !== undefined) {
      target.ssl_ca_cert_bundle = source.ssl_ca_cert_bundle;
    }
  });
  return merged;
};

export const missingSecretParameterNames = (
  requiredSecretInputs: ComputeProvisioningRequiredSecretInput[] = [],
  secretParams: Record<string, any> = {}
): string[] => requiredSecretInputs
  .flatMap(input => input.parameter_names)
  .filter(name => !secretParams[name]);

export const externalStorageNameByClientRef = (
  reviewedExternalStorages: ComputeProvisioningReviewedExternalStorage[] = []
): Record<string, string> => Object.fromEntries(
  reviewedExternalStorages.map(externalStorage => [externalStorage.client_ref, externalStorage.name])
);

export interface FlattenedProblem {
  code: string;
  detail: string;
  phase: string | null;
  field_path: string | null;
  node_id?: ID;
  interface_name?: string;
  errors_truncated?: boolean;
}

export const flattenProblems = (
  problems: (ComputeProvisioningProblem | ComputeProvisioningNestedProblem)[] = []
): FlattenedProblem[] => problems.flatMap(problem => [
  {
    code: problem.code,
    detail: problem.detail,
    phase: problem.phase,
    field_path: problem.field_path,
    node_id: problem.node_id,
    interface_name: problem.interface_name,
    errors_truncated: problem.errors_truncated
  },
  ...flattenProblems(problem.errors || [])
]);

export const hasTruncatedProblems = (problems: FlattenedProblem[], truncated = false): boolean => (
  truncated || problems.some(problem => problem.errors_truncated)
);

const FEATURE_TO_SERVICE: Partial<Record<string, keyof ComputeServicesModel>> = {
  maas: 'maas',
  'infra-optim': 'infra-optim',
  k8saas: 'k8saas',
  lbaas: 'lbaas',
  metering: 'metering'
};

const hydrateExternalStorage = (
  reviewed: ComputeProvisioningReviewedExternalStorage
): ExternalStorageFormModel => {
  const params = {
    use_multipath_for_image_xfer: false,
    ...(reviewed.params || {})
  } as unknown as ExternalStorageFormModel['params'];
  const volumeDriver = (params.volume_driver as string) || '';
  const { protocol } = determineDriverInfo(volumeDriver, { params } as any);
  const managementAddress = resolveManagementAddress(params);
  const isNetApp = volumeDriver === VOLUME_DRIVERS.NETAPP;
  let transportType: string = params.driver_ssl_cert_verify ? TRANSPORT_TYPE.HTTPS : TRANSPORT_TYPE.HTTP;
  if (isNetApp) {
    transportType = (params.netapp_transport_type as string) || TRANSPORT_TYPE.HTTPS;
  }

  return {
    name: reviewed.name,
    enabled: reviewed.enabled ?? true,
    protocol: (protocol as PROTOCOL) || undefined,
    external_storage: volumeDriver ? (VOLUME_DRIVER_TO_STORAGE_MAP[volumeDriver] || EXTERNAL_STORAGES.OPENSTACK_COMPATIBLE) : undefined,
    managementAddress,
    transport_type: transportType as ExternalStorageFormModel['transport_type'],
    params: params as ExternalStorageFormModel['params'],
    secret_params: {},
    volume_driver_params: [],
    volume_driver_secret_params: []
  };
};

export const mapRequestToDraft = (
  request: ComputeProvisioningReviewedRequest
): Partial<ComputeClusterFormModel> => {
  const nodeIds = (request.storage_cluster?.nodes || []).map(node => node.node_id);

  const nodeNetworkRolesets: NodeNetworkRolesets = {};
  const nodeInterfaceEdits: NodeInterfaceEdits = {};
  (request.network?.nodes || []).forEach((node) => {
    (node.interfaces || []).forEach((iface) => {
      if (iface.roles_set) {
        nodeNetworkRolesets[node.node_id] = nodeNetworkRolesets[node.node_id] || {};
        nodeNetworkRolesets[node.node_id][iface.name] = iface.roles_set;
      }
      nodeInterfaceEdits[node.node_id] = nodeInterfaceEdits[node.node_id] || {};
      nodeInterfaceEdits[node.node_id][iface.name] = {
        ipv4: iface.ipv4 || [],
        ipv6: iface.ipv6 || [],
        gw4: iface.gw4 || null,
        gw6: iface.gw6 || null,
        dhcp4_enabled: !!iface.dhcp4_enabled,
        dhcp6_enabled: !!iface.dhcp6_enabled,
        mtu: iface.mtu,
        ignore_auto_routes_v4: !!iface.ignore_auto_routes_v4,
        ignore_auto_routes_v6: !!iface.ignore_auto_routes_v6
      };
    });
  });

  const rolesetTrafficRoles: RolesetTrafficRoles = {};
  (request.network?.role_sets || []).forEach((roleSet) => {
    rolesetTrafficRoles[roleSet.id] = roleSet.roles;
  });

  const externalNetwork = request.compute_cluster?.external_network;
  const subnet = externalNetwork?.subnet;

  const services: ComputeServicesModel = {
    maas: false,
    'infra-optim': false,
    k8saas: false,
    lbaas: false,
    metering: false
  };
  (request.compute_cluster?.enable_features || []).forEach((feature) => {
    const serviceName = FEATURE_TO_SERVICE[feature];
    if (serviceName) {
      services[serviceName] = true;
    }
  });

  const reviewedExternalStorage = request.external_storages?.[0] || null;

  return {
    clusterName: request.storage_cluster?.cluster_name || '',
    nodeIds: nodeIds.length ? nodeIds : null,
    vmHaPlanned: !!request.vm_ha_planned,
    nodeNetworkRolesets,
    nodeInterfaceEdits,
    rolesetTrafficRoles,
    trafficRolesCustomized: true,
    ipManagement: !!subnet,
    physicalNetwork: externalNetwork?.roles_set_id || null,
    physicalType: externalNetwork?.vlan_id ? NETWORK_PHYSICAL_TYPES.VLAN : NETWORK_PHYSICAL_TYPES.UNTAGGED,
    vlanId: externalNetwork?.vlan_id ?? null,
    cidr: subnet?.cidr || '',
    gateway: subnet?.gateway || '',
    dhcpAndDns: {
      dhcp: subnet?.enable_dhcp ?? true,
      pools: (subnet?.allocation_pools || []).map(pool => ({
        startAddress: pool.start_address,
        endAddress: pool.end_address
      })),
      dns: (subnet?.dns_servers || []).map(value => ({ value }))
    },
    services,
    configureExternalStorage: !!reviewedExternalStorage,
    externalStorage: reviewedExternalStorage ? hydrateExternalStorage(reviewedExternalStorage) : null,
    externalStorageClientRef: reviewedExternalStorage?.client_ref || null,
    multipathConfig: request.multipath || null,
    storagePolicies: request.storage_policies ? cloneDeep(request.storage_policies) : null,
    requestedStoragePolicies: getRequestedStoragePolicies(
      request.storage_policies?.items || [],
      ['redundancy', 'failure_domain', 'tier_selector']
    )
  };
};
